import React, { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/api";
import { downloadFile, uploadFile } from "@/lib/download";
import { PageTitle, EmptyState, Badge } from "@/components/ui-kit";
import InspireBanner from "@/components/InspireBanner";
import { UserCog, Plus, Pencil, Trash2, Printer, FileDown, Upload, AlertTriangle, ChevronDown, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { playSuccess } from "@/lib/sound";
import { useAuth } from "@/context/AuthContext";
import SupervisorSchoolsDialog from "@/components/supervisor/SupervisorSchoolsDialog";
import { MapPin } from "lucide-react";

const empty = { name: "", email: "", phone: "", role: "coordinator", school_id: "" };
const F = ({ label, children }) => (<div><label className="text-xs text-stone-500 mb-1 block">{label}</label>{children}</div>);
const inputCls = "w-full px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-400";

function SchoolPicker({ schools, value, onChange }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = schools.find((s) => s.id === value);
  const nq = q.trim();
  const filtered = nq ? schools.filter((s) => (s.name || "").includes(nq)) : schools;
  return (
    <div className="relative">
      <input
        data-testid="user-school-search"
        value={open ? q : (selected?.name || "")}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { setQ(""); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="اكتب اسم المدرسة للبحث..."
        className={inputCls}
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-amber-400/40 bg-amber-50 shadow-lg" data-testid="school-picker-list">
          {filtered.slice(0, 80).map((s) => (
            <button key={s.id} type="button" data-testid={`school-opt-${s.id}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(s.id); setOpen(false); setQ(""); }}
              className={`w-full text-right px-3 py-2 text-sm hover:bg-amber-200/60 border-b border-amber-400/10 ${s.id === value ? "bg-amber-200/50 font-semibold" : "text-stone-800"}`}>
              {s.name} <span className="text-[10px] text-stone-400">({s.type})</span>
            </button>
          ))}
          {!filtered.length && <div className="px-3 py-3 text-sm text-stone-500">لا توجد مدرسة بهذا الاسم</div>}
        </div>
      )}
    </div>
  );
}

export default function Users() {
  const [items, setItems] = useState([]);
  const [schools, setSchools] = useState([]);
  const [noCoord, setNoCoord] = useState([]);
  const [showNoCoord, setShowNoCoord] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [q, setQ] = useState("");
  const { user: me } = useAuth();
  const isGeneralAdmin = me?.role === "admin" && !me?.is_supervisor && !me?.is_monitor;
  const [supFor, setSupFor] = useState(null);

  const load = () => {
    api.get("/users").then((r) => setItems(r.data)).catch(() => {});
    api.get("/schools").then((r) => setSchools(r.data)).catch(() => {});
    api.get("/admin/schools-without-coordinator").then((r) => setNoCoord(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openNewForSchool = (schoolId) => { setForm({ ...empty, school_id: schoolId }); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ ...empty, ...u, role: u.is_monitor ? "monitor" : u.is_supervisor ? "supervisor" : u.role }); setEditId(u.id); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.email) return toast.error("الاسم والبريد مطلوبان");
    if ((form.role === "coordinator" || form.role === "monitor" || form.role === "supervisor") && !form.phone) return toast.error("رقم الجوال مطلوب (يُستخدم لتسجيل الدخول)");
    if (form.role === "coordinator" && !form.school_id) return toast.error("اختر المدرسة للمنسق");
    try {
      if (editId) await api.put(`/users/${editId}`, form);
      else await api.post("/users", form);
      playSuccess();
      toast.success(editId ? "تم التحديث" : "تم إنشاء الحساب");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async () => {
    try { await api.delete(`/users/${delId}`); toast.success("تم الحذف"); setDelId(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doExport = async () => { try { await downloadFile("/users/coordinators/export", "coordinator_logins.xlsx"); } catch { toast.error("تعذر التصدير"); } };
  const downloadTemplate = async () => { try { await downloadFile("/users/import/template", "coordinators_template.xlsx"); } catch { toast.error("تعذر تنزيل القالب"); } };
  const doImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const res = await uploadFile("/users/import/preview", file);
      setPendingFile(file); setPreview(res);
    } catch (err) { toast.error(err.message || "تعذر قراءة الملف"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const confirmImport = async () => {
    if (!pendingFile) return;
    setImporting(true);
    try {
      const res = await uploadFile("/users/import", pendingFile);
      toast.success(`تم: ${res.added} جديد، ${res.updated || 0} محدّث${res.skipped ? `، ${res.skipped} متخطى` : ""}`);
      if (res.errors?.length) toast.error(res.errors.slice(0, 3).join(" | "));
      setPreview(null); setPendingFile(null); load();
    } catch (err) { toast.error(err.message || "تعذر الاستيراد"); }
    finally { setImporting(false); }
  };

  const printLoginCard = (u) => {
    const w = window.open("", "_blank", "width=700,height=800");
    if (!w) return toast.error("يرجى السماح بالنوافذ المنبثقة للطباعة");
    w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>بطاقة دخول المنسق</title>
    <style>body{font-family:'Cairo',Arial,sans-serif;background:#f6efe4;padding:40px;color:#2b2016}
    .card{max-width:520px;margin:0 auto;background:#fff;border:2px solid #D4AF37;border-radius:20px;padding:32px;box-shadow:0 10px 30px rgba(0,0,0,.08)}
    h1{color:#b8860b;font-size:22px;margin:0 0 4px}.sub{color:#7a6a55;font-size:13px;margin:0 0 20px}
    .row{display:flex;justify-content:space-between;padding:12px 0;border-bottom:1px dashed #e3d5b8}
    .k{color:#7a6a55;font-size:13px}.v{font-weight:700;font-size:15px}
    .note{margin-top:20px;background:#eaf6ff;border:1px solid #bfe3ff;border-radius:12px;padding:12px;font-size:12px;color:#25566f;line-height:1.7}</style>
    </head><body><div class="card">
    <h1>بطاقة دخول منسق التطوع</h1><p class="sub">ملف الإنجاز التطوعي — ملف إنجاز الأعمال التطوعية في مدارس منطقة حائل</p>
    <div class="row"><span class="k">اسم المنسق</span><span class="v">${u.name || ""}</span></div>
    <div class="row"><span class="k">المدرسة</span><span class="v">${u.school_name || "-"}</span></div>
    <div class="row"><span class="k">البريد الإلكتروني</span><span class="v">${u.email || ""}</span></div>
    <div class="row"><span class="k">رقم الجوال</span><span class="v">${u.phone || ""}</span></div>
    <div class="note">للدخول: افتح النظام ← اختر بوابة «منسق التطوع» ← أدخل البريد الإلكتروني ورقم الجوال المذكورين أعلاه.</div>
    </div><script>window.onload=function(){window.print();}</script></body></html>`);
    w.document.close();
  };

  const roleAr = { admin: "مدير", coordinator: "منسق" };

  const nq = q.trim();
  const filtered = nq
    ? items.filter((u) => [u.name, u.email, u.phone, u.school_name].some((v) => (v || "").includes(nq)))
    : items;

  return (
    <div>
      <InspireBanner />

      <PageTitle title="إدارة المستخدمين" subtitle="حسابات المديرين ومنسقي التطوع" icon={UserCog}
        actions={<>
          <button data-testid="template-users-btn" onClick={downloadTemplate} className="glass glass-hover px-3 py-2 rounded-xl flex items-center gap-2 text-stone-800 text-sm"><FileDown size={17} /> قالب Excel</button>
          <button data-testid="import-users-btn" onClick={() => fileRef.current?.click()} className="glass glass-hover px-3 py-2 rounded-xl flex items-center gap-2 text-stone-800 text-sm"><Upload size={17} /> استيراد Excel</button>
          <input ref={fileRef} type="file" accept=".xlsx" onChange={doImport} className="hidden" data-testid="users-file-input" />
          <button data-testid="export-logins-btn" onClick={doExport} className="glass glass-hover px-3 py-2 rounded-xl flex items-center gap-2 text-stone-800 text-sm"><FileDown size={17} /> تصدير بيانات الدخول</button>
          <button data-testid="add-user-btn" onClick={openNew} className="btn-gold px-4 py-2 rounded-xl flex items-center gap-2"><Plus size={18} /> مستخدم جديد</button>
        </>}
      />
      {noCoord.length > 0 && (
        <div className="glass rounded-2xl p-4 mb-4 border border-amber-400/40" data-testid="no-coord-banner">
          <button onClick={() => setShowNoCoord((v) => !v)} className="w-full flex items-center justify-between text-right" data-testid="toggle-no-coord">
            <span className="flex items-center gap-2 text-amber-700 font-semibold"><AlertTriangle size={18} /> مدارس بلا منسق ({noCoord.length})</span>
            <ChevronDown size={18} className={`transition-transform ${showNoCoord ? "rotate-180" : ""} text-stone-500`} />
          </button>
          {showNoCoord && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
              {noCoord.map((s) => (
                <div key={s.id} data-testid={`no-coord-${s.id}`} className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-400/20 rounded-lg px-3 py-2">
                  <span className="text-sm text-stone-700 truncate">{s.name} <span className="text-[10px] text-stone-400">({s.type})</span></span>
                  <button data-testid={`add-coord-${s.id}`} onClick={() => openNewForSchool(s.id)} className="btn-gold px-2 py-1 rounded-lg text-xs whitespace-nowrap flex items-center gap-1"><Plus size={13} /> منسق</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {!items.length ? <EmptyState text="لا يوجد مستخدمون" icon={UserCog} /> : (
        <div className="glass rounded-2xl p-4 overflow-x-auto">
          <div className="relative mb-3 max-w-md">
            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input data-testid="user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو البريد أو الجوال أو المدرسة..." className={`${inputCls} pr-9`} />
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-stone-500 text-right border-b border-amber-400/15">
              <th className="py-3 px-2 font-medium">الاسم</th><th className="py-3 px-2 font-medium">البريد</th>
              <th className="py-3 px-2 font-medium">الدور</th><th className="py-3 px-2 font-medium">المدرسة</th>
              <th className="py-3 px-2 font-medium">إجراءات</th>
            </tr></thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-b border-amber-400/10 hover:bg-amber-100/50 transition">
                  <td className="py-3 px-2 text-stone-800 font-medium">{u.name}</td>
                  <td className="py-3 px-2 text-stone-600">{u.email}</td>
                  <td className="py-3 px-2"><Badge className={u.is_monitor ? "bg-purple-500/15 text-purple-700 border-purple-400/30" : u.role === "admin" ? "bg-amber-500/20 text-amber-700 border-amber-400/40" : "bg-blue-500/15 text-blue-700 border-blue-400/30"}>{u.is_supervisor ? "مشرف" : u.is_monitor ? "فريق المتابعة" : roleAr[u.role]}</Badge></td>
                  <td className="py-3 px-2 text-stone-600">{u.school_name || "-"}</td>
                  <td className="py-3 px-2"><div className="flex gap-1">
                    {u.role === "coordinator" && <button data-testid={`print-card-${u.id}`} onClick={() => printLoginCard(u)} className="p-1.5 rounded-lg hover:bg-sky-500/20 text-sky-600" title="بطاقة دخول للطباعة"><Printer size={16} /></button>}
                    {u.is_supervisor && isGeneralAdmin && <button data-testid={`sup-schools-${u.id}`} onClick={() => setSupFor(u)} className="px-2 py-1 rounded-lg hover:bg-emerald-500/15 text-emerald-700 text-xs flex items-center gap-1 whitespace-nowrap" title="ربط المدارس بالمشرف"><MapPin size={15} /> مدارسه ({(u.supervised_school_ids || []).length})</button>}
                    <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="p-1.5 rounded-lg hover:bg-amber-500/20 text-amber-600"><Pencil size={16} /></button>
                    {u.role !== "admin" && <button data-testid={`del-user-${u.id}`} onClick={() => setDelId(u.id)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-700"><Trash2 size={16} /></button>}
                  </div></td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={5} className="py-6 text-center text-stone-500" data-testid="user-search-empty">لا نتائج مطابقة للبحث</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-lg" dir="rtl">
          <DialogHeader><DialogTitle className="gold-text font-head">{editId ? "تعديل مستخدم" : "مستخدم جديد"}</DialogTitle><DialogDescription className="text-stone-500 text-xs">للمنسق: البريد الإلكتروني ورقم الجوال معاً هما بيانات تسجيل الدخول، لذا يجب تعبئتهما بدقة.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="الاسم"><input data-testid="user-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></F>
            <F label="البريد الإلكتروني"><input data-testid="user-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></F>
            <F label="رقم الجوال (لدخول المنسق) *">
              <input data-testid="user-phone" value={form.phone} onChange={(e) => set("phone", e.target.value.replace(/[^0-9+]/g, ""))} className={inputCls} placeholder="05xxxxxxxx" inputMode="numeric" />
              <p className="text-[10px] text-stone-400 mt-1">أرقام فقط (مثال: 0555xxxxxx). يُستخدم مع البريد لتسجيل دخول المنسق.</p>
            </F>
            <F label="الدور"><select data-testid="user-role" value={form.role} onChange={(e) => set("role", e.target.value)} className={inputCls}><option value="coordinator">منسق تطوع</option><option value="supervisor">مشرف</option><option value="monitor">فريق المتابعة</option><option value="admin">مدير</option></select></F>
            {form.role === "coordinator" && <F label="المدرسة (ابحث بالاسم)"><SchoolPicker schools={schools} value={form.school_id} onChange={(id) => set("school_id", id)} /></F>}
          </div>
          <DialogFooter><button data-testid="save-user-btn" onClick={save} className="btn-gold px-6 py-2 rounded-xl">حفظ</button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent className="glass border-amber-400/30 text-stone-800" dir="rtl">
          <AlertDialogHeader><AlertDialogTitle className="text-stone-800">تأكيد الحذف</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="bg-amber-50 border-amber-400/30 text-stone-800">إلغاء</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700">حذف</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { setPreview(null); setPendingFile(null); } }}>
        <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-3xl" dir="rtl">
          <DialogHeader><DialogTitle className="gold-text font-head">معاينة استيراد المستخدمين</DialogTitle></DialogHeader>
          {preview && (
            <>
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-700">جديد: {preview.new || 0}</span>
                <span className="px-3 py-1 rounded-full bg-sky-500/15 text-sky-700">سيُحدَّث: {preview.update || 0}</span>
                <span className="px-3 py-1 rounded-full bg-amber-500/15 text-amber-700">سيُتخطى: {preview.skip}</span>
                <span className="px-3 py-1 rounded-full bg-stone-500/15 text-stone-700">الإجمالي: {preview.total}</span>
              </div>
              <div className="max-h-80 overflow-y-auto mt-3">
                <table className="w-full text-xs">
                  <thead><tr className="text-stone-500 text-right border-b border-amber-400/15">
                    <th className="py-2 px-2 font-medium">الاسم</th><th className="py-2 px-2 font-medium">البريد</th><th className="py-2 px-2 font-medium">الجوال</th><th className="py-2 px-2 font-medium">الدور</th><th className="py-2 px-2 font-medium">المدرسة</th><th className="py-2 px-2 font-medium">الحالة</th><th className="py-2 px-2 font-medium">ملاحظات</th>
                  </tr></thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} data-testid={`user-preview-row-${i}`} className="border-b border-amber-400/10">
                        <td className="py-2 px-2 text-stone-800 font-medium">{r.name}</td>
                        <td className="py-2 px-2 text-stone-600">{r.email || "—"}</td>
                        <td className="py-2 px-2 text-stone-600">{r.phone || "—"}</td>
                        <td className="py-2 px-2 text-stone-600">{r.role_ar}</td>
                        <td className="py-2 px-2 text-stone-600">{r.school_name || "—"}</td>
                        <td className="py-2 px-2">{r.status === "skip" ? <span className="text-amber-700">سيُتخطى</span> : r.status === "update" ? <span className="text-sky-700">سيُحدَّث</span> : <span className="text-emerald-700">جديد</span>}</td>
                        <td className="py-2 px-2 text-stone-500">{r.note || "—"}</td>
                      </tr>
                    ))}
                    {!preview.rows.length && <tr><td colSpan={7} className="py-6 text-center text-stone-500">لا توجد صفوف صالحة في الملف</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <DialogFooter>
            <button data-testid="cancel-user-import-btn" onClick={() => { setPreview(null); setPendingFile(null); }} className="glass glass-hover px-5 py-2 rounded-xl text-stone-700">إلغاء</button>
            <button data-testid="confirm-user-import-btn" onClick={confirmImport} disabled={importing || !preview?.valid} className="btn-gold px-6 py-2 rounded-xl disabled:opacity-50">{importing ? "جارٍ الاستيراد..." : `تأكيد استيراد ${preview?.valid || 0} منسق`}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {supFor && <SupervisorSchoolsDialog sup={supFor} schools={schools} onClose={() => setSupFor(null)} onSaved={load} />}
    </div>
  );
}
